import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { BookOpen, Trophy, Target, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

interface LearningAnalyticsProps {
  companyId: string;
}

interface LearningModule {
  id: string;
  title: string;
  xp_reward: number;
  created_at: string;
  completions: number;
  avgScore: number;
  totalAssigned: number;
}

interface LearnerProgress {
  worker_id: string;
  worker_name: string;
  team: string;
  completed_modules: number;
  total_assigned: number;
  avg_score: number;
  last_completed: string | null;
}

export default function LearningAnalytics({ companyId }: LearningAnalyticsProps) {
  const [modules, setModules] = useState<LearningModule[]>([]);
  const [learners, setLearners] = useState<LearnerProgress[]>([]);
  const [stats, setStats] = useState({
    totalModules: 0,
    totalCompletions: 0,
    avgCompletionRate: 0,
    avgScore: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (companyId) {
      loadAnalytics();
    }
  }, [companyId]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      
      // Load learning modules
      const { data: modulesData, error: modulesError } = await supabase
        .from('learning_modules')
        .select('id, title, xp_reward, created_at')
        .eq('company_id', companyId)
        .eq('is_active', true);

      if (modulesError) throw modulesError;

      // Load worker quests for micro_learning type - this is where actual progress is tracked
      const { data: questsData, error: questsError } = await supabase
        .from('worker_quests')
        .select(`
          id,
          worker_id,
          status,
          completed_at,
          response_data,
          template_id,
          quest_templates!inner(
            id,
            intervention_type,
            title,
            xp_reward,
            content,
            company_id
          )
        `)
        .eq('quest_templates.intervention_type', 'micro_learning')
        .eq('quest_templates.company_id', companyId);

      if (questsError) throw questsError;

      // Load worker profiles for names
      const workerIds = [...new Set((questsData || []).map(q => q.worker_id))];
      const { data: workersData } = await supabase
        .from('worker_profiles')
        .select('id, display_name, team')
        .in('id', workerIds.length > 0 ? workerIds : ['no-id']);

      const workerMap = new Map(
        (workersData || []).map(w => [w.id, { display_name: w.display_name, team: w.team }])
      );

      // Process quests to get module-level stats
      const moduleStatsMap = new Map<string, { completions: number; scores: number[]; assigned: number }>();
      
      (questsData || []).forEach((quest: any) => {
        const templateId = quest.template_id;
        const moduleId = quest.quest_templates?.content?.module_id || templateId;
        
        if (!moduleStatsMap.has(moduleId)) {
          moduleStatsMap.set(moduleId, { completions: 0, scores: [], assigned: 0 });
        }
        
        const stat = moduleStatsMap.get(moduleId)!;
        stat.assigned++;
        
        if (quest.status === 'completed') {
          stat.completions++;
          // Extract score from response_data
          const responseData = quest.response_data as Record<string, any> || {};
          const score = responseData.quiz_score ?? responseData.score ?? responseData.finalScore;
          if (typeof score === 'number') {
            stat.scores.push(score);
          }
        }
      });

      // Build module stats
      const moduleStats: LearningModule[] = (modulesData || []).map(mod => {
        const stats = moduleStatsMap.get(mod.id) || { completions: 0, scores: [], assigned: 0 };
        return {
          ...mod,
          completions: stats.completions,
          avgScore: stats.scores.length > 0 
            ? Math.round(stats.scores.reduce((a, b) => a + b, 0) / stats.scores.length) 
            : 0,
          totalAssigned: stats.assigned
        };
      });

      // Also add modules from quest_templates that might not be in learning_modules
      const templateModules = new Map<string, { title: string; xp_reward: number; created_at: string }>();
      (questsData || []).forEach((quest: any) => {
        const templateId = quest.template_id;
        if (!moduleStats.find(m => m.id === templateId) && !templateModules.has(templateId)) {
          templateModules.set(templateId, {
            title: quest.quest_templates?.title || 'Learning Module',
            xp_reward: quest.quest_templates?.xp_reward || 0,
            created_at: new Date().toISOString()
          });
        }
      });

      templateModules.forEach((mod, id) => {
        const stats = moduleStatsMap.get(id) || { completions: 0, scores: [], assigned: 0 };
        moduleStats.push({
          id,
          title: mod.title,
          xp_reward: mod.xp_reward,
          created_at: mod.created_at,
          completions: stats.completions,
          avgScore: stats.scores.length > 0 
            ? Math.round(stats.scores.reduce((a, b) => a + b, 0) / stats.scores.length) 
            : 0,
          totalAssigned: stats.assigned
        });
      });

      setModules(moduleStats);

      // Process learner progress
      const learnerMap = new Map<string, LearnerProgress>();
      
      (questsData || []).forEach((quest: any) => {
        const workerId = quest.worker_id;
        const worker = workerMap.get(workerId);
        
        if (!learnerMap.has(workerId)) {
          learnerMap.set(workerId, {
            worker_id: workerId,
            worker_name: worker?.display_name || 'Unknown',
            team: worker?.team || '-',
            completed_modules: 0,
            total_assigned: 0,
            avg_score: 0,
            last_completed: null
          });
        }
        
        const learner = learnerMap.get(workerId)!;
        learner.total_assigned++;
        
        if (quest.status === 'completed') {
          learner.completed_modules++;
          
          const responseData = quest.response_data as Record<string, any> || {};
          const score = responseData.quiz_score ?? responseData.score ?? responseData.finalScore;
          if (typeof score === 'number') {
            // Running average
            learner.avg_score = (learner.avg_score * (learner.completed_modules - 1) + score) / learner.completed_modules;
          }
          
          if (!learner.last_completed || (quest.completed_at && new Date(quest.completed_at) > new Date(learner.last_completed))) {
            learner.last_completed = quest.completed_at;
          }
        }
      });

      const learnerArray = Array.from(learnerMap.values()).sort((a, b) => b.completed_modules - a.completed_modules);
      setLearners(learnerArray);

      // Calculate overall stats
      const totalCompletions = moduleStats.reduce((acc, m) => acc + m.completions, 0);
      const totalAssigned = moduleStats.reduce((acc, m) => acc + m.totalAssigned, 0);
      const allScores: number[] = [];
      
      (questsData || []).forEach((quest: any) => {
        if (quest.status === 'completed') {
          const responseData = quest.response_data as Record<string, any> || {};
          const score = responseData.quiz_score ?? responseData.score ?? responseData.finalScore;
          if (typeof score === 'number') {
            allScores.push(score);
          }
        }
      });

      setStats({
        totalModules: moduleStats.length,
        totalCompletions,
        avgCompletionRate: totalAssigned > 0 ? Math.round((totalCompletions / totalAssigned) * 100) : 0,
        avgScore: allScores.length > 0 ? Math.round(allScores.reduce((a, b) => a + b, 0) / allScores.length) : 0
      });

    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load learning analytics');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-amber-500';
    return 'text-red-500';
  };

  const completionRateData = modules.map(m => ({
    name: m.title.length > 20 ? m.title.substring(0, 17) + '...' : m.title,
    completion: m.totalAssigned > 0 ? Math.round((m.completions / m.totalAssigned) * 100) : 0,
    score: m.avgScore
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <BookOpen className="h-5 w-5 text-green-500" />
            <div>
              <p className="text-2xl font-bold">{stats.totalModules}</p>
              <p className="text-sm text-muted-foreground">Modules</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-blue-500" />
            <div>
              <p className="text-2xl font-bold">{stats.totalCompletions}</p>
              <p className="text-sm text-muted-foreground">Completions</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Target className="h-5 w-5 text-amber-500" />
            <div>
              <p className="text-2xl font-bold">{stats.avgCompletionRate}%</p>
              <p className="text-sm text-muted-foreground">Completion Rate</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Trophy className="h-5 w-5 text-purple-500" />
            <div>
              <p className="text-2xl font-bold">{stats.avgScore}%</p>
              <p className="text-sm text-muted-foreground">Avg Quiz Score</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Module Performance Chart */}
      {modules.length > 0 && (
        <Card className="p-4">
          <h3 className="font-semibold mb-4">Module Completion & Scores</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={completionRateData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" fontSize={11} angle={-15} textAnchor="end" height={60} />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Bar dataKey="completion" name="Completion %" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                <Bar dataKey="score" name="Avg Score %" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Module Details */}
      <div className="space-y-3">
        <h3 className="font-semibold">Module Details</h3>
        {modules.length === 0 ? (
          <Card className="p-8 text-center">
            <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No learning modules created yet</p>
          </Card>
        ) : (
          modules.map((module, index) => (
            <motion.div
              key={module.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-semibold">{module.title}</h4>
                    <p className="text-sm text-muted-foreground">
                      Created {new Date(module.created_at).toLocaleDateString('id-ID')}
                    </p>
                  </div>
                  <Badge variant="outline">+{module.xp_reward} XP</Badge>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Completion</span>
                    <span className="font-medium">{module.completions}/{module.totalAssigned}</span>
                  </div>
                  <Progress 
                    value={module.totalAssigned > 0 ? (module.completions / module.totalAssigned) * 100 : 0} 
                    className="h-2" 
                  />
                  
                  <div className="flex items-center justify-between text-sm mt-2">
                    <span className="text-muted-foreground">Avg Quiz Score</span>
                    <span className={`font-medium ${getScoreColor(module.avgScore)}`}>
                      {module.avgScore}%
                    </span>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))
        )}
      </div>

      {/* Learner Leaderboard */}
      {learners.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-semibold">Learner Progress</h3>
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="p-3 text-left">Worker</th>
                  <th className="p-3 text-left">Team</th>
                  <th className="p-3 text-center">Completed</th>
                  <th className="p-3 text-center">Avg Score</th>
                  <th className="p-3 text-left">Last Completed</th>
                </tr>
              </thead>
              <tbody>
                {learners.slice(0, 10).map((learner, idx) => (
                  <tr key={learner.worker_id} className="border-t">
                    <td className="p-3 font-medium">
                      {idx < 3 && learner.completed_modules > 0 && (
                        <span className="mr-2">
                          {idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}
                        </span>
                      )}
                      {learner.worker_name}
                    </td>
                    <td className="p-3 text-muted-foreground">{learner.team}</td>
                    <td className="p-3 text-center">
                      <Badge variant={learner.completed_modules === learner.total_assigned ? 'default' : 'outline'}>
                        {learner.completed_modules}/{learner.total_assigned}
                      </Badge>
                    </td>
                    <td className={`p-3 text-center font-medium ${getScoreColor(learner.avg_score)}`}>
                      {Math.round(learner.avg_score)}%
                    </td>
                    <td className="p-3 text-muted-foreground">
                      {learner.last_completed 
                        ? new Date(learner.last_completed).toLocaleDateString('id-ID') 
                        : '-'
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}
    </div>
  );
}